import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import SensorsOutlinedIcon from "@mui/icons-material/SensorsOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";

import { getDevices, createSensorChannel } from "../api/sensorChannelsApi";
import { prettyError } from "../utils/prettyError";
import { Colors } from "../../utils/Colors";

const CHANNEL_TYPE_OPTIONS = [
  { value: "DRIP", label: "Drip" },
  { value: "URINE", label: "Urine" },
  { value: "TEMPERATURE", label: "Temperature" },
  { value: "PRESSURE", label: "Pressure" },
  { value: "OTHER", label: "Other" },
];

const initialForm = {
  device: "",
  channel_key: "",
  channel_type: "",
  label: "",
  is_enabled: true,
};

export default function SensorChannelCreateForm() {
  const [form, setForm] = React.useState(initialForm);
  const [devices, setDevices] = React.useState([]);
  const [loadingDevices, setLoadingDevices] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [success, setSuccess] = React.useState("");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    loadDevices();
  }, []);

  const loadDevices = async () => {
    try {
      setLoadingDevices(true);
      setError("");

      const response = await getDevices();

      const deviceList = Array.isArray(response)
        ? response
        : Array.isArray(response?.results)
        ? response.results
        : [];

      setDevices(deviceList);
    } catch (err) {
      setError(prettyError(err));
      setDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value =
      field === "is_enabled" ? event.target.checked : event.target.value;

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getDeviceId = (item) => item?.device_id ?? item?.id ?? "";

  const getDeviceLabel = (item) => {
    const serial = item?.device_serial || "";
    const model = item?.device_model || "";

    if (serial && model) return `${serial} - ${model}`;
    if (serial) return serial;
    if (model) return model;
    return `Device ${getDeviceId(item)}`;
  };

  const validate = () => {
    if (!form.device) return "Device is required.";
    if (!form.channel_key.trim()) return "Channel key is required.";
    if (!form.channel_type) return "Channel type is required.";
    return "";
  };

  const buildPayload = () => {
    return {
      device: form.device,
      channel_key: form.channel_key.trim(),
      channel_type: form.channel_type,
      label: form.label.trim() || null,
      is_enabled: form.is_enabled,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccess("");
    setError("");

    const validationMessage = validate();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    try {
      setSaving(true);
      await createSensorChannel(buildPayload());
      setSuccess("Sensor channel created successfully.");
      setForm(initialForm);
    } catch (err) {
      setError(prettyError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setForm(initialForm);
    setSuccess("");
    setError("");
  };

  return (
    <Box
      sx={{
        backgroundColor: Colors.bg.app,
        minHeight: "100vh",
        py: 3,
        px: { xs: 1.5, sm: 2, md: 3 },
      }}
    >
      <Box sx={{ maxWidth: 1000, mx: "auto" }}>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${Colors.border.light}`,
            overflow: "hidden",
            boxShadow: "0 8px 18px rgba(11,60,63,0.08)",
          }}
        >
          <Box
            sx={{
              background: `linear-gradient(90deg, ${Colors.brand.primary} 0%, ${Colors.brand.primaryDark} 100%)`,
              px: 3,
              py: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: "#fff",
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              Sensor Channel Registration
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.88)",
                mt: 0.5,
                fontSize: "0.82rem",
              }}
            >
              Create a sensor channel for a Fludee device.
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: Colors.text.header,
                mb: 0.5,
              }}
            >
              Channel Details
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: Colors.text.muted,
                mb: 2,
              }}
            >
              Fill the required details below.
            </Typography>

            <Divider sx={{ mb: 2 }} />

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <FormRow
                  label="Device Serial"
                  field={
                    <TextField
                      fullWidth
                      select
                      value={form.device}
                      onChange={handleChange("device")}
                      disabled={loadingDevices}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MemoryOutlinedIcon sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    >
                      <MenuItem value="">
                        {loadingDevices ? "Loading devices..." : "Select device serial"}
                      </MenuItem>

                      {devices.map((item) => (
                        <MenuItem
                          key={String(getDeviceId(item))}
                          value={String(getDeviceId(item))}
                        >
                          {getDeviceLabel(item)}
                        </MenuItem>
                      ))}
                    </TextField>
                  }
                />

                <FormRow
                  label="Channel Key"
                  field={
                    <TextField
                      fullWidth
                      value={form.channel_key}
                      onChange={handleChange("channel_key")}
                      placeholder="Example: DRIP_A"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SensorsOutlinedIcon sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    />
                  }
                />

                <FormRow
                  label="Channel Type"
                  field={
                    <TextField
                      fullWidth
                      select
                      value={form.channel_type}
                      onChange={handleChange("channel_type")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CategoryOutlinedIcon sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    >
                      <MenuItem value="">Select channel type</MenuItem>

                      {CHANNEL_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  }
                />

                <FormRow
                  label="Label"
                  field={
                    <TextField
                      fullWidth
                      value={form.label}
                      onChange={handleChange("label")}
                      placeholder="Optional label"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LabelOutlinedIcon sx={{ color: Colors.text.icon }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    />
                  }
                />

                <FormRow
                  label="Enabled"
                  field={
                    <Box
                      sx={{
                        minHeight: 46,
                        border: `1px solid ${Colors.border.input}`,
                        borderRadius: 2,
                        px: 1.5,
                        display: "flex",
                        alignItems: "center",
                        backgroundColor: "#fff",
                      }}
                    >
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Switch
                            checked={form.is_enabled}
                            onChange={handleChange("is_enabled")}
                          />
                        }
                        label={form.is_enabled ? "Enabled" : "Disabled"}
                      />
                    </Box>
                  }
                />

                <Grid container spacing={1.5} sx={{ pt: 1 }}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      disabled={saving || loadingDevices}
                      startIcon={
                        saving ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <SaveOutlinedIcon />
                        )
                      }
                      sx={submitBtnSx}
                    >
                      {saving ? "Creating..." : "Create Channel"}
                    </Button>
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Button
                      fullWidth
                      type="button"
                      variant="outlined"
                      onClick={handleReset}
                      startIcon={<RestartAltOutlinedIcon />}
                      sx={resetBtnSx}
                    >
                      Reset
                    </Button>
                  </Grid>
                </Grid>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

function FormRow({ label, field }) {
  return (
    <Grid container spacing={1.5} alignItems="center">
      <Grid size={{ xs: 12, md: 3 }}>
        <Typography
          sx={{
            fontWeight: 700,
            color: Colors.text.header,
            textAlign: { xs: "left", md: "right" },
            pr: { md: 1 },
            fontSize: "0.95rem",
          }}
        >
          {label}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 9 }}>
        {field}
      </Grid>
    </Grid>
  );
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 46,
    borderRadius: 2,
    backgroundColor: "#fff",
    "& fieldset": {
      borderColor: Colors.border.input,
    },
    "&:hover fieldset": {
      borderColor: Colors.brand.primary,
    },
    "&.Mui-focused fieldset": {
      borderColor: Colors.brand.primary,
      borderWidth: 1.5,
    },
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
  },
  "& .MuiInputBase-input": {
    color: Colors.text.body,
    fontSize: "0.95rem",
  },
};

const submitBtnSx = {
  height: 46,
  borderRadius: 2,
  textTransform: "uppercase",
  fontWeight: 800,
  letterSpacing: 0.3,
  backgroundColor: Colors.brand.primary,
  "&:hover": {
    backgroundColor: Colors.brand.primaryDark,
  },
};

const resetBtnSx = {
  height: 46,
  borderRadius: 2,
  textTransform: "uppercase",
  fontWeight: 800,
  letterSpacing: 0.3,
  borderColor: Colors.border.input,
  color: Colors.brand.primary,
  "&:hover": {
    borderColor: Colors.brand.primary,
    backgroundColor: Colors.bg.hover,
  },
};